import { IBalusterGeometry, IRailingGeometry, IStepGeometry, IStringerType, ISupportPosts, IVec3 } from '@stair-craft/common/util';

import { clearBalusterSpacing } from '../shared-baluster-count/baluster-count';
import { IPlanPoint } from '../shared-plan-geometry/plan-geometry';
import { insideOutline, treadFootprint } from '../shared-tread-footprint/tread-footprint';
import { gapFillers, widestRailGap } from './rail-gaps';

/** How many times the refill runs over the gaps its own sliding fillers left. */
const REFILL_ROUNDS: number = 3;

/** A walking surface in plan: the tread outline as drawn, and its top. */
interface ISurface {
    readonly outline: IPlanPoint[];
    readonly top: number;
}

/** Heights within this much count as one flat seat. */
const FLAT: number = 0.5;
/** Samples per side of an upright's foot. */
const GRID: number = 5;
/** How much a height difference counts against a plan distance when picking the rail segment. */
const HEIGHT_HINT_WEIGHT: number = 0.05;
/** Search step of a shift along the railing (mm). */
const SHIFT_STEP: number = 1;

interface ISeat {
    readonly base: number;
    /** Whether a tread carries the whole foot (not the floor or slab the upright was set on). */
    readonly onTread: boolean;
}

/** The highest tread top over a plan point; −Infinity where no tread is. */
function surfaceTop(surfaces: readonly ISurface[], point: IPlanPoint): number {
    let top: number = -Infinity;
    for (const surface of surfaces) {
        if (surface.top > top && insideOutline(surface.outline, point)) {
            top = surface.top;
        }
    }
    return top;
}

/** The seat under a square foot, or null when it straddles two levels (a nose, a tread end). */
function seatAt(surfaces: readonly ISurface[], center: IPlanPoint, width: number, fallback: number): ISeat | null {
    // The foot is sampled a hair inside its edges: touching a neighbour is not standing on it.
    const half: number = width / 2 - 0.25;
    let low: number = Infinity;
    let high: number = -Infinity;
    let onTread: boolean = true;
    for (let i: number = 0; i < GRID; i++) {
        for (let j: number = 0; j < GRID; j++) {
            const point: IPlanPoint = {
                x: center.x - half + (2 * half * i) / (GRID - 1),
                z: center.z - half + (2 * half * j) / (GRID - 1),
            };
            let top: number = surfaceTop(surfaces, point);
            if (top === -Infinity) {
                top = fallback;
                onTread = false;
            }
            low = Math.min(low, top);
            high = Math.max(high, top);
        }
    }
    return high - low <= FLAT ? { onTread, base: high } : null;
}

/**
 * Handrail centre height above a plan point and the rail direction there: the segment nearest
 * in plan, ties between levels (a spiral that turns past a full circle) broken by the height
 * the upright reached before.
 */
function railAt(points: readonly IVec3[], at: IPlanPoint, hintY: number): { y: number; direction: IPlanPoint } | null {
    let best: { distance: number; y: number; direction: IPlanPoint } | null = null;
    for (let i: number = 0; i < points.length - 1; i++) {
        const a: IVec3 = points[i];
        const b: IVec3 = points[i + 1];
        const dx: number = b.x - a.x;
        const dz: number = b.z - a.z;
        const lengthSq: number = dx * dx + dz * dz;
        if (lengthSq < 1e-6) {
            continue;
        }
        const t: number = Math.max(0, Math.min(1, ((at.x - a.x) * dx + (at.z - a.z) * dz) / lengthSq));
        const y: number = a.y + t * (b.y - a.y);
        const distance: number = Math.hypot(a.x + t * dx - at.x, a.z + t * dz - at.z) + HEIGHT_HINT_WEIGHT * Math.abs(y - hintY);
        if (!best || distance < best.distance) {
            const length: number = Math.sqrt(lengthSq);
            best = { distance, y, direction: { x: dx / length, z: dz / length } };
        }
    }
    return best ? { y: best.y, direction: best.direction } : null;
}

/** The plan square an upright of this width covers at a centre. */
interface IFoot {
    readonly x: number;
    readonly z: number;
    readonly half: number;
    /** The height range the upright fills: feet under one another (a spiral past a full turn) do not meet. */
    readonly low: number;
    readonly high: number;
}

/** Whether two feet overlap in plan by more than a hair. */
function feetOverlap(a: IFoot, b: IFoot): boolean {
    const reach: number = a.half + b.half - FLAT;
    return Math.abs(a.x - b.x) < reach && Math.abs(a.z - b.z) < reach && a.low < b.high && b.low < a.high;
}

/** The highest tread top anywhere under a foot; −Infinity where no tread is. */
function highestUnder(surfaces: readonly ISurface[], center: IPlanPoint, width: number): number {
    const half: number = width / 2 - 0.25;
    let top: number = -Infinity;
    for (let i: number = 0; i < GRID; i++) {
        for (let j: number = 0; j < GRID; j++) {
            const point: IPlanPoint = {
                x: center.x - half + (2 * half * i) / (GRID - 1),
                z: center.z - half + (2 * half * j) / (GRID - 1),
            };
            top = Math.max(top, surfaceTop(surfaces, point));
        }
    }
    return top;
}

/** What one upright is settled against: the treads, its rail and the feet already placed. */
interface ISettleContext {
    readonly surfaces: readonly ISurface[];
    readonly rail: readonly IVec3[];
    readonly taken: IFoot[];
}

/** A candidate place for an upright and the seat it would get there. */
interface IPlace {
    readonly point: IPlanPoint;
    readonly seat: ISeat;
}

/** One upright being settled: its foot, its reach to the rail top and the treads that can carry it. */
interface IUprightProbe {
    readonly width: number;
    readonly fallback: number;
    readonly top: number;
    readonly surfaces: readonly ISurface[];
    readonly context: ISettleContext;
}

function footAt(probe: IUprightProbe, point: IPlanPoint): IFoot {
    return { half: probe.width / 2, low: probe.fallback, high: probe.top, x: point.x, z: point.z };
}

/** The flat seat at a point, or null when the foot straddles two levels or a placed upright. */
function placeAt(probe: IUprightProbe, point: IPlanPoint): IPlace | null {
    const foot: IFoot = footAt(probe, point);
    if (probe.context.taken.some((other: IFoot) => feetOverlap(other, foot))) {
        return null;
    }
    const seat: ISeat | null = seatAt(probe.surfaces, point, probe.width, probe.fallback);
    return seat ? { point, seat } : null;
}

/** The nearest flat free place along the rail within reach, a tread preferred at equal distance. */
function slideAlongRail(probe: IUprightProbe, center: IPlanPoint, reach: number): IPlace | null {
    const along: { y: number; direction: IPlanPoint } | null = railAt(probe.context.rail, center, probe.top);
    if (!along) {
        return null;
    }
    for (let shift: number = SHIFT_STEP; shift <= reach; shift += SHIFT_STEP) {
        const candidates: IPlace[] = [shift, -shift].flatMap((signed: number) => {
            const place: IPlace | null = placeAt(probe, {
                x: center.x + along.direction.x * signed,
                z: center.z + along.direction.z * signed,
            });
            return place ? [place] : [];
        });
        const chosen: IPlace | undefined = candidates.find((candidate: IPlace) => candidate.seat.onTread) ?? candidates.at(0);
        if (chosen) {
            return chosen;
        }
    }
    return null;
}

/** Sets an upright at a point on a base, its top on the handrail above that point. */
function setUpright(upright: IBalusterGeometry, probe: IUprightProbe, point: IPlanPoint, base: number): void {
    const railTop: number = railAt(probe.context.rail, point, probe.top)?.y ?? probe.top;
    upright.position.x = point.x;
    upright.position.z = point.z;
    upright.position.y = base;
    upright.height = Math.max(0, railTop - base);
    probe.context.taken.push(footAt(probe, point));
}

/**
 * Seats one upright and reports whether it stays. Where its foot straddles two levels or a
 * placed upright, it slides along the rail to the nearest spot that is flat and free —
 * preferring a tread over the floor or slab — and its height is reset so the top meets the
 * handrail. With no such spot in reach it stands where it is, on the highest tread under its
 * foot (an upright at a winder's inner corner), or is dropped when another upright already
 * takes its place.
 */
function settleUpright(upright: IBalusterGeometry, width: number, reach: number, context: ISettleContext): boolean {
    const center: IPlanPoint = { x: upright.position.x, z: upright.position.z };
    const top: number = upright.position.y + upright.height;
    const probe: IUprightProbe = {
        width,
        top,
        context,
        fallback: upright.position.y,
        // A tread above the handrail (the other flight of a U turn) is no seat and no obstacle.
        surfaces: context.surfaces.filter((surface: ISurface) => surface.top < top - FLAT),
    };
    const place: IPlace | null = placeAt(probe, center) ?? slideAlongRail(probe, center, reach);
    if (place) {
        setUpright(upright, probe, place.point, place.seat.base);
        return true;
    }
    const foot: IFoot = footAt(probe, center);
    if (context.taken.some((other: IFoot) => feetOverlap(other, foot))) {
        return false;
    }
    setUpright(upright, probe, center, Math.max(probe.fallback, highestUnder(probe.surfaces, center, width)));
    return true;
}

/** Where a gap filler starts: the highest tread under it below the rail, else the base of the nearest upright. */
function fillerBase(surfaces: readonly ISurface[], uprights: readonly IBalusterGeometry[], point: IVec3, width: number): number {
    const below: ISurface[] = surfaces.filter((surface: ISurface) => surface.top < point.y - FLAT);
    const tread: number = highestUnder(below, point, width);
    if (tread > -Infinity) {
        return tread;
    }
    let nearest: { distance: number; base: number } = { distance: Infinity, base: 0 };
    for (const upright of uprights) {
        const distance: number = Math.hypot(upright.position.x - point.x, upright.position.z - point.z);
        if (distance < nearest.distance) {
            nearest = { distance, base: upright.position.y };
        }
    }
    return nearest.base;
}

/**
 * Closes the gaps that settling opened wider than `limit`. A filler that slid while settling can
 * open a gap of its own, so the refill runs again over what it left, a bounded number of times.
 */
function refillGaps(
    railing: IRailingGeometry,
    limit: number,
    surfaces: readonly ISurface[],
    taken: IFoot[],
    supports?: ISupportPosts
): void {
    const context: ISettleContext = { surfaces, taken, rail: railing.handrail.points };
    const reach: number = (limit + railing.balusterWidth) / 2;
    for (let round: number = 0; round < REFILL_ROUNDS; round++) {
        const uprights: IBalusterGeometry[] = [...railing.posts, ...railing.balusters];
        const base: (point: IVec3) => number = (point: IVec3): number => fillerBase(surfaces, uprights, point, railing.balusterWidth);
        const fillers: IBalusterGeometry[] = gapFillers(railing, limit, base, supports).filter((filler: IBalusterGeometry) =>
            settleUpright(filler, railing.balusterWidth, reach, context)
        );
        if (fillers.length === 0) {
            return;
        }
        railing.balusters = [...railing.balusters, ...fillers];
    }
}

/**
 * Sets every baluster and post of the stair railings on a flat seat: a tread (its nose
 * included), or the floor or slab it was placed on. An upright whose foot would straddle a
 * nose, a tread end or another upright slides along the rail by at most half a baluster pitch
 * (a post by its own width), so no foot hangs over a drop and none runs through a nose or a
 * post. Posts are seated first, and a post or baluster with no free place left is dropped.
 * Upper-floor railings stand on the slab and are left alone.
 */
export function settleStairRailings(
    railings: readonly IRailingGeometry[],
    steps: readonly IStepGeometry[],
    stringerType: IStringerType,
    balusterSpacing: number,
    supports?: ISupportPosts
): void {
    const surfaces: ISurface[] = steps.map((step: IStepGeometry) => ({
        outline: treadFootprint(step, stringerType),
        top: step.position.y,
    }));
    // A turn's support posts (SC-CALC-093) stand first: no upright takes their place.
    const taken: IFoot[] = (supports?.posts ?? []).map((post: IBalusterGeometry) => ({
        x: post.position.x,
        z: post.position.z,
        half: (supports?.width ?? 0) / 2,
        low: post.position.y,
        high: post.position.y + post.height,
    }));
    const stair: IRailingGeometry[] = railings.filter((railing: IRailingGeometry) => railing.kind !== 'upper');
    // Sliding and dropping must not open a gap wider than the allowed clear spacing.
    const limit: number = clearBalusterSpacing(balusterSpacing);
    // A flight lays one baluster per tread, wider apart than the spacing; refilling keeps that gap.
    const laidGaps: number[] = stair.map((railing: IRailingGeometry) => widestRailGap({ ...railing, posts: [] }));
    for (const railing of stair) {
        const context: ISettleContext = { surfaces, taken, rail: railing.handrail.points };
        const ends: IVec3[] = [railing.handrail.points[0], railing.handrail.points[railing.handrail.points.length - 1]];
        railing.posts = railing.posts.filter((post: IBalusterGeometry) => {
            // A post on the floor stays where it is: the treads it meets are notched around it.
            if (post.position.y < FLAT) {
                context.taken.push({ x: post.position.x, z: post.position.z, half: railing.postWidth / 2, low: 0, high: post.height });
                return true;
            }
            // A post at an end of the rail takes the rail end along when it slides.
            const end: IVec3 | undefined = ends.find(
                (point: IVec3) => Math.hypot(point.x - post.position.x, point.z - post.position.z) < FLAT
            );
            const kept: boolean = settleUpright(post, railing.postWidth, railing.postWidth, context);
            if (end) {
                end.x = post.position.x;
                end.z = post.position.z;
            }
            return kept;
        });
    }
    for (const railing of stair) {
        const context: ISettleContext = { surfaces, taken, rail: railing.handrail.points };
        const reach: number = (limit + railing.balusterWidth) / 2;
        railing.balusters = railing.balusters.filter((baluster: IBalusterGeometry) =>
            settleUpright(baluster, railing.balusterWidth, reach, context)
        );
    }
    stair.forEach((railing: IRailingGeometry, index: number) => {
        refillGaps(railing, Math.max(limit, laidGaps[index]), surfaces, taken, supports);
    });
}
